import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Ban, Loader2, Lock, Mail, ShieldCheck, Timer, Trash2, Unlock } from "lucide-react";
import { AdminTopbar } from "@/components/admin/AdminLayout";
import { ModulePanel, DataTable } from "@/components/admin/AdminPrimitives";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/lib/i18n";
import {
  useUtilisateurs,
  useBloquerUtilisateur,
  useDebloquerUtilisateur,
  useLeverVerrouTemporaire,
  useSupprimerUtilisateur,
} from "@/hooks/useUtilisateurs";
import { useInvitations, useCreateInvitation, useAnnulerInvitation, useSupprimerInvitation } from "@/hooks/useInvitations";
import { useBloquerPartenaire } from "@/pages/admin/RequireAdmin";
import type { UtilisateurDto } from "@/types/utilisateur.types";

function Roles() {
  useBloquerPartenaire();
  const { t } = useI18n();
  const { data: utilisateurs, isLoading: loadingUsers } = useUtilisateurs();
  const { data: invitations, isLoading: loadingInvitations } = useInvitations();
  const createInvitation = useCreateInvitation();
  const annulerInvitation = useAnnulerInvitation();
  const bloquerUtilisateur = useBloquerUtilisateur();
  const debloquerUtilisateur = useDebloquerUtilisateur();
  const leverVerrouTemporaire = useLeverVerrouTemporaire();
  const supprimerUtilisateur = useSupprimerUtilisateur();

  const ROLES_INVITABLES = [
    { value: "ADMIN", label: t("admin.users.role.admin") },
    { value: "REDACTEUR", label: t("admin.users.role.redacteur") },
    { value: "PARTENAIRE", label: t("admin.users.role.partenaire") },
  ];

  const STATUT_INVITATION_LABEL: Record<string, string> = {
    EN_ATTENTE: t("admin.users.invitations.status.pending"),
    ACCEPTEE: t("admin.users.invitations.status.accepted"),
    EXPIREE: t("admin.users.invitations.status.expired"),
    ANNULEE: t("admin.users.invitations.status.cancelled"),
  };

  const utilisateurList = useMemo(() => (Array.isArray(utilisateurs) ? utilisateurs : []), [utilisateurs]);
  const invitationList = useMemo(() => (Array.isArray(invitations) ? invitations : []), [invitations]);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("REDACTEUR");
  const [deleteTarget, setDeleteTarget] = useState<UtilisateurDto | null>(null);
  const [deleteInvitationTarget, setDeleteInvitationTarget] = useState<{ id: number; email: string } | null>(null);
  const [unblockTarget, setUnblockTarget] = useState<UtilisateurDto | null>(null);
  const [unblockMessage, setUnblockMessage] = useState("");
  const supprimerInvitation = useSupprimerInvitation();

  const handleBloquer = (u: UtilisateurDto) => {
    bloquerUtilisateur.mutate(u.id, {
      onSuccess: () => toast.success(t("admin.users.toast.blocked", { email: u.email })),
      onError: (e: any) => toast.error(e?.response?.data?.message || t("admin.users.toast.actionError")),
    });
  };

  const handleLeverVerrou = (u: UtilisateurDto) => {
    leverVerrouTemporaire.mutate(u.id, {
      onSuccess: () => toast.success(t("admin.users.toast.lockLifted", { email: u.email })),
      onError: (e: any) => toast.error(e?.response?.data?.message || t("admin.users.toast.actionError")),
    });
  };

  // Le verrou anti-bruteforce (15 min après 3 codes erronés) est indépendant du
  // statut persisté : un compte "Actif" peut quand même être temporairement
  // inaccessible, sans quoi ni l'utilisateur ni l'admin ne peuvent le savoir.
  const minutesRestantes = (iso: string) =>
    Math.max(1, Math.ceil((new Date(iso).getTime() - Date.now()) / 60000));

  const openUnblockDialog = (u: UtilisateurDto) => {
    setUnblockTarget(u);
    setUnblockMessage("");
  };

  const confirmUnblock = () => {
    if (!unblockTarget || !unblockMessage.trim()) return;
    debloquerUtilisateur.mutate(
      { id: unblockTarget.id, message: unblockMessage.trim() },
      {
        onSuccess: () => {
          toast.success(t("admin.users.toast.unblocked", { email: unblockTarget.email }));
          setUnblockTarget(null);
        },
        onError: (e: any) => toast.error(e?.response?.data?.message || t("admin.users.toast.actionError")),
      }
    );
  };

  const handleAnnulerInvitation = (id: number, emailInv: string) => {
    annulerInvitation.mutate(id, {
      onSuccess: () => toast.success(t("admin.users.toast.invitationCancelled", { email: emailInv })),
      onError: (e: any) => toast.error(e?.response?.data?.message || t("admin.users.toast.invitationCancelError")),
    });
  };

  const confirmDeleteInvitation = () => {
    if (!deleteInvitationTarget) return;
    supprimerInvitation.mutate(deleteInvitationTarget.id, {
      onSuccess: () => {
        toast.success(`Invitation de ${deleteInvitationTarget.email} supprimée`);
        setDeleteInvitationTarget(null);
      },
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || "Impossible de supprimer cette invitation");
        setDeleteInvitationTarget(null);
      },
    });
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    supprimerUtilisateur.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t("admin.users.toast.deleted", { email: deleteTarget.email }));
        setDeleteTarget(null);
      },
      onError: (e: any) => {
        toast.error(e?.response?.data?.message || t("admin.users.toast.deleteError"));
        setDeleteTarget(null);
      },
    });
  };

  const handleInvite = (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    createInvitation.mutate(
      { email: email.trim(), role },
      {
        onSuccess: () => {
          toast.success(t("admin.users.toast.invitationSent", { email: email.trim() }));
          setEmail("");
        },
        onError: (e: any) => toast.error(e?.response?.data?.message || t("admin.users.toast.inviteError")),
      }
    );
  };

  return (
    <>
      <AdminTopbar title={t("admin.users.title")} subtitle={t("admin.users.subtitle")} />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <ModulePanel title={t("admin.users.invite.title")}>
          <form onSubmit={handleInvite} className="grid gap-4 md:grid-cols-[1fr_180px_auto]">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.users.col.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("admin.users.invite.emailPlaceholder")}
                required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("admin.users.col.role")}</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                {ROLES_INVITABLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={createInvitation.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
              >
                {createInvitation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {t("admin.users.invite.submit")}
              </button>
            </div>
          </form>
        </ModulePanel>

        <ModulePanel title={t("admin.users.invitations.title", { count: invitationList.length })}>
          {loadingInvitations ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("admin.common.loading")}
            </div>
          ) : invitationList.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">{t("admin.users.invitations.empty")}</div>
          ) : (
            <DataTable
              columns={[
                t("admin.users.col.email"),
                t("admin.users.col.role"),
                t("admin.common.col.status"),
                t("admin.users.col.expires"),
                t("admin.common.col.actions"),
              ]}
              rows={invitationList.map((inv: any) => [
                inv.email,
                inv.role,
                <span
                  className={
                    inv.statut === "ACCEPTEE"
                      ? "text-verified font-semibold"
                      : inv.statut === "EXPIREE" || inv.statut === "ANNULEE"
                      ? "text-muted-foreground"
                      : "text-alert font-semibold"
                  }
                >
                  {STATUT_INVITATION_LABEL[inv.statut] ?? inv.statut}
                </span>,
                inv.expireLe ? new Date(inv.expireLe).toLocaleDateString() : "—",
                <div className="flex flex-wrap items-center gap-2">
                  {inv.statut === "EN_ATTENTE" && (
                    <button
                      onClick={() => handleAnnulerInvitation(inv.id, inv.email)}
                      disabled={annulerInvitation.isPending}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert disabled:opacity-60"
                    >
                      <Ban className="h-3.5 w-3.5" /> {t("admin.users.invitations.cancel")}
                    </button>
                  )}
                  <button
                    onClick={() => setDeleteInvitationTarget({ id: inv.id, email: inv.email })}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> {t("admin.common.delete")}
                  </button>
                </div>,
              ])}
            />
          )}
        </ModulePanel>

        <ModulePanel title={t("admin.users.list.title", { count: utilisateurList.length })}>
          {loadingUsers ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("admin.common.loading")}
            </div>
          ) : (
            <DataTable
              columns={[
                t("admin.users.col.email"),
                t("admin.users.col.nom"),
                t("admin.users.col.roles"),
                t("admin.users.col.code"),
                t("admin.common.col.status"),
                t("admin.common.col.actions"),
              ]}
              rows={utilisateurList.map((u: UtilisateurDto & { nomAffichage?: string; roles: string[]; code?: string }) => {
                const estAdmin = (u.roles || []).includes("ADMIN");
                const estBloque = u.statut === "BLOQUE";
                const verrouTemporaire = !!u.bloqueTemporairementJusqua;
                return [
                  u.email,
                  u.nom || u.nomAffichage || "—",
                  <span className="inline-flex items-center gap-1 text-xs font-semibold">
                    <ShieldCheck className="h-3.5 w-3.5 text-verified" /> {(u.roles || []).join(", ")}
                  </span>,
                  <span className="font-mono text-xs text-muted-foreground">{u.code || "—"}</span>,
                  <div className="flex flex-col gap-1">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${estBloque ? "text-alert" : "text-verified"}`}>
                      {estBloque ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
                      {estBloque ? t("admin.users.status.blocked") : t("admin.users.status.active")}
                      {estBloque && u.tentativesEchouees > 0 && (
                        <span className="text-muted-foreground font-normal">
                          ({t("admin.users.status.attempts", { count: u.tentativesEchouees })})
                        </span>
                      )}
                    </span>
                    {verrouTemporaire && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" title={t("admin.users.lock.tooltip")}>
                        <Timer className="h-3.5 w-3.5" />
                        {t("admin.users.lock.label", { minutes: minutesRestantes(u.bloqueTemporairementJusqua!) })}
                      </span>
                    )}
                  </div>,
                  estAdmin ? (
                    verrouTemporaire ? (
                      <button
                        onClick={() => handleLeverVerrou(u)}
                        disabled={leverVerrouTemporaire.isPending}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-amber-600 disabled:opacity-60"
                      >
                        <Timer className="h-3.5 w-3.5" /> {t("admin.users.lock.action")}
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => (estBloque ? openUnblockDialog(u) : handleBloquer(u))}
                        disabled={bloquerUtilisateur.isPending || debloquerUtilisateur.isPending}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs disabled:opacity-60"
                      >
                        {estBloque ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        {estBloque ? t("admin.users.action.unblock") : t("admin.users.action.block")}
                      </button>
                      {verrouTemporaire && (
                        <button
                          onClick={() => handleLeverVerrou(u)}
                          disabled={leverVerrouTemporaire.isPending}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-amber-600 disabled:opacity-60"
                        >
                          <Timer className="h-3.5 w-3.5" /> {t("admin.users.lock.action")}
                        </button>
                      )}
                      <button
                        onClick={() => setDeleteTarget(u)}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs text-alert"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {t("admin.common.delete")}
                      </button>
                    </div>
                  ),
                ];
              })}
            />
          )}
        </ModulePanel>
      </div>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("admin.users.deleteConfirm.title")}
        description={deleteTarget ? t("admin.users.deleteConfirm.description", { email: deleteTarget.email }) : undefined}
        onConfirm={confirmDelete}
        loading={supprimerUtilisateur.isPending}
      />

      <ConfirmDeleteDialog
        open={!!deleteInvitationTarget}
        onOpenChange={(open) => !open && setDeleteInvitationTarget(null)}
        title="Supprimer cette invitation ?"
        description={deleteInvitationTarget ? `L'invitation envoyée à ${deleteInvitationTarget.email} sera définitivement supprimée.` : undefined}
        onConfirm={confirmDeleteInvitation}
        loading={supprimerInvitation.isPending}
      />

      <Dialog open={!!unblockTarget} onOpenChange={(open) => !open && setUnblockTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin.users.unblockDialog.title", { email: unblockTarget?.email ?? "" })}</DialogTitle>
            <DialogDescription>
              {t("admin.users.unblockDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={unblockMessage}
            onChange={(e) => setUnblockMessage(e.target.value)}
            placeholder={t("admin.users.unblockDialog.placeholder")}
            rows={4}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
          />
          <DialogFooter>
            <button
              type="button"
              onClick={() => setUnblockTarget(null)}
              className="rounded-md border border-border px-4 py-2 text-sm"
            >
              {t("admin.common.cancel")}
            </button>
            <button
              type="button"
              onClick={confirmUnblock}
              disabled={!unblockMessage.trim() || debloquerUtilisateur.isPending}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background disabled:opacity-60"
            >
              {debloquerUtilisateur.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("admin.users.unblockDialog.submit")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Roles;
